import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../lib/api";

export default function Weight() {
  const [weights, setWeights] = useState([]);
  const [weight, setWeight] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [unit, setUnit] = useState("kg");

  const load = async () => setWeights((await api.get("/weights")).data);
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!weight) return;
    try {
      await api.post("/weights", { date, weight: Number(weight), unit });
      toast.success("Weight logged");
      setWeight(""); load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (id) => { await api.delete(`/weights/${id}`); load(); };

  const data = weights.map((w) => ({ date: w.date?.slice(5), weight: w.weight }));
  const latest = weights.length ? weights[weights.length - 1].weight : null;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#007AFF] mb-2">Progress</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Body Weight</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <form onSubmit={add} className="p-6 bg-[#121212] border border-white/10 space-y-4">
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Log Entry</h3>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Date</label>
            <input data-testid="weight-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#007AFF] focus:outline-none" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Weight</label>
              <input data-testid="weight-value" type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="75.0" className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#007AFF] focus:outline-none" />
            </div>
            <div className="w-24">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Unit</label>
              <select data-testid="weight-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#007AFF] focus:outline-none">
                <option value="kg">kg</option>
                <option value="lb">lb</option>
              </select>
            </div>
          </div>
          <button data-testid="save-weight" className="w-full bg-[#007AFF] hover:bg-[#1a8cff] text-white font-bold uppercase tracking-wide px-6 py-3 flex items-center justify-center gap-2 transition-transform duration-150 hover:-translate-y-0.5">
            <Plus size={18} /> Add Entry
          </button>
        </form>

        <div className="lg:col-span-2 p-6 bg-[#121212] border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display font-bold text-lg uppercase tracking-tight">Trend</h3>
            {latest && <span className="font-display font-black text-3xl tabular-nums text-[#007AFF]">{latest}<span className="text-sm text-white/40 ml-1">{weights[weights.length - 1]?.unit}</span></span>}
          </div>
          {data.length > 1 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data}>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                <Line type="monotone" dataKey="weight" stroke="#007AFF" strokeWidth={2.5} dot={{ r: 3, fill: "#007AFF" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[240px] flex items-center justify-center text-white/40 border border-dashed border-white/10 text-sm">Add at least 2 entries to see your trend</div>
          )}
        </div>
      </div>

      {weights.length > 0 && (
        <div className="bg-[#121212] border border-white/10" data-testid="weight-list">
          {[...weights].reverse().map((w) => (
            <div key={w.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0">
              <span className="text-white/60 text-sm">{new Date(w.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
              <div className="flex items-center gap-4">
                <span className="font-display font-bold text-lg tabular-nums">{w.weight} {w.unit}</span>
                <button data-testid={`delete-weight-${w.id}`} onClick={() => remove(w.id)} className="text-white/30 hover:text-[#FF3B30]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
